import React from "react"
import { useSelector, useDispatch } from "react-redux"

import {
    IDLE, PENDING,
    PROVEN_NO, PROVEN_IN_USE, PRIOR_USE,
} from "const"
import {
    QidModel,
    QidModelSafetyFunction,
} from "server/models"
import { deepCopy } from "client/utils"
import {
    StoreType,
    QidModelList,
    ChangeQidModelStatus,

    setQidModelList,
    setQidModelChangeStatusModel,
    setQidModelDeleteModel,
    setQidModelDeleteSafetyFunction,
} from "client/store"
import {
    TableColumn,
    TablePopover,
    TablePopoverData,
    Link,
    DateFormatter,
    StatusSwitcher,
    DeleteButton,
} from "client/components"

import { openModel, openSafetyFunction } from "./open"

const Columns = (productEditAccess: boolean) => {
    const modelList = useSelector((state: StoreType) => state.qidModel.modelList)
    const changeStatusModel = useSelector((state: StoreType) => state.qidModel.changeStatusModel)

    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const changeModelStatus = (model: QidModel) => {
        if (changeStatusModel) return

        dispatch(setQidModelChangeStatusModel(model))
        ChangeQidModelStatus(model).then((status) => {
            const modelListCopy: QidModelList = deepCopy(modelList)
            modelListCopy[model.id] = {
                ...modelListCopy[model.id],
                status,
            }
            dispatch(setQidModelList(modelListCopy))
            dispatch(setQidModelChangeStatusModel(null))
        })
    }

    const deleteModel = (e: React.MouseEvent, model: QidModel) => {
        e.stopPropagation()
        dispatch(setQidModelDeleteModel(model))
    }

    const deleteSafetyFunction = (e: React.MouseEvent, safetyFunction: QidModelSafetyFunction) => {
        e.stopPropagation()
        dispatch(setQidModelDeleteSafetyFunction(safetyFunction))
    }

    const columns: TableColumn[] = [
        {
            field: "name",
            header: lang.model,
            sortable: true,
            cellMinWidth: "150px",
            style: { paddingLeft: "32px" },
            formatter: (model: QidModel) => (
                productEditAccess
                    ? (
                        <Link
                            href="/#"
                            onClick={(e) => {
                                e.stopPropagation()
                                openModel(model.id, dispatch)
                            }}
                        >
                            {model.name}
                        </Link>
                    )
                    : model.name
            ),
        },
        {
            field: "safetyFunction",
            header: lang.safetyFunction,
            cellMinWidth: "200px",
            style: { paddingLeft: "32px" },
            formatter: (safetyFunction: QidModelSafetyFunction) => (
                productEditAccess
                    ? (
                        <Link
                            href="/#"
                            onClick={(e) => {
                                e.stopPropagation()
                                openSafetyFunction(safetyFunction.modelId, safetyFunction.id, dispatch)
                            }}
                        >
                            {safetyFunction.name}
                        </Link>
                    )
                    : safetyFunction.name
            ),
        },
        {
            field: "type",
            header: lang.type,
            sortable: true,
            cellMinWidth: "100px",
            formatter: (item: QidModel | QidModelSafetyFunction) => item.type,
        },
        {
            field: "mode",
            header: lang.mode,
            sortable: true,
            cellMinWidth: "150px",
            formatter: (item: QidModel | QidModelSafetyFunction) => {
                const dataList: TablePopoverData[] = []

                if (item.lowDemandMode) {
                    dataList.push({
                        id: `${item.id}lowDemandMode`,
                        title: lang.modeLowDemand,
                    })
                }
                if (item.highDemandMode) {
                    dataList.push({
                        id: `${item.id}highDemandMode`,
                        title: lang.modeHighDemand,
                    })
                }
                if (item.continuousMode) {
                    dataList.push({
                        id: `${item.id}continuousMode`,
                        title: lang.modeContinuous,
                    })
                }

                return (
                    <TablePopover
                        id={`${item.id}_${item.name}_modeList`}
                        dataList={dataList}
                        placement="right"
                        title={lang.modes}
                        editAccess={false}
                    />
                )
            },
        },
        {
            field: "logicSolver",
            header: lang.logicSolver,
            sortable: true,
            cellMinWidth: "150px",
            formatter: (item: QidModel | QidModelSafetyFunction) => (item.logicSolver ? lang.yes : lang.no),
        },
        {
            field: "provenInUse",
            header: lang.provenReliabilityData,
            sortable: true,
            cellMinWidth: "200px",
            formatter: (item: QidModel | QidModelSafetyFunction) => {
                if (item.provenInUse === PROVEN_NO) return lang.no
                if (item.provenInUse === PROVEN_IN_USE) return lang.provenInUse
                if (item.provenInUse === PRIOR_USE) return lang.priorUse
                return null
            },
        },
    ]

    if (productEditAccess) {
        columns.push(
            {
                field: "status",
                header: lang.status,
                sortable: true,
                cellMinWidth: "100px",
                centerAlign: true,
                formatter: (item: QidModel | QidModelSafetyFunction) => {
                    const model = item as QidModel
                    if (model.productId) { // check that the row is "QidModel"
                        return (
                            <StatusSwitcher
                                status={changeStatusModel && changeStatusModel.id === model.id ? PENDING : model.status}
                                disabled={!productEditAccess}
                                onClick={(e) => {
                                    e.stopPropagation()
                                    changeModelStatus(model)
                                }}
                            />
                        )
                    }
                    return null
                },
            },
            {
                field: "createdAt",
                header: lang.created,
                sortable: true,
                cellMinWidth: "200px",
                formatter: (item: QidModel | QidModelSafetyFunction) => <DateFormatter date={item.createdAt} />,
            },
            {
                field: "delete",
                centerAlign: true,
                formatter: (item: QidModel | QidModelSafetyFunction) => {
                    const model = item as QidModel
                    const safetyFunction = item as QidModelSafetyFunction

                    if (model.productId) { // check that the row is "QidModel"
                        return (
                            <DeleteButton
                                status={IDLE}
                                onClick={(e) => deleteModel(e, model)}
                            />
                        )
                    }
                    if (safetyFunction.modelId) { // check that the row is "QidModelSafetyFunction"
                        return (
                            <DeleteButton
                                status={IDLE}
                                onClick={(e) => deleteSafetyFunction(e, safetyFunction)}
                            />
                        )
                    }
                    return null
                },
            },
        )
    }

    return columns
}
export default Columns
